import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { SettingsSideMenuComponent } from '@components/editor/settings-side-menu/settings-side-menu.component';
import { SelectedElementsService } from '@service/canvas/selected-elements.service';
import { Mimic } from 'icreate/common/models/mimic';
import { Subject, lastValueFrom, take, takeUntil } from 'rxjs';
import * as _ from 'lodash';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { CanvasService } from '@service/canvas/canvas.service';
import { EditorService, MenuTabs } from '@service/editor.service';
import { Library } from 'icreate/common/models/library';
import { RootItem } from 'icreate/common/models/mimic-folder';
import { AutosavePage } from '@service/auto-save-controller.service';

@Component({
    selector: 'app-editor-side-menu',
    templateUrl: './editor-side-menu.component.html',
    styleUrls: ['./editor-side-menu.component.css'],
    standalone: false,
})
export class EditorSideMenuComponent implements OnInit, OnDestroy, OnChanges {
    @Input() selectedMimic: Mimic;
    @Input() userCanEdit: boolean;
    @Input() libraryList!: Library[];
    @Input() rootList: RootItem[];

    @ViewChild('settingsMenu') settingsMenu: SettingsSideMenuComponent;
    @ViewChild('exitSettingsConfirmation') exitSettingsConfirmation: ConfirmationWindowComponent;

    EDITOR_TAB = Object.values(MenuTabs);

    selectedMenu: MenuTabs = MenuTabs.DESIGN;
    selectedElements: SVGGraphicsElement[] = [];

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private selectionService: SelectedElementsService,
        private canvasService: CanvasService,
        private editorService: EditorService
    ) {}

    ngOnInit(): void {
        this.editorService.currentSelectedTab$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((tab: MenuTabs) => {
                this.selectedMenu = tab;
            });

        this.selectionService
            .onElementsSelected()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(async (elem: SVGGraphicsElement[]) => {
                if (this.editorService.getCurrentMimicIsDeleted()) {
                    this.editorService.setCurrentSelectedTab(MenuTabs.DESIGN);
                    this.selectedElements = _.cloneDeep(elem);
                    return;
                }
                if (_.isEqual(this.selectedElements, elem)) {
                    return;
                }
                if (this.selectedMenu === MenuTabs.SETTINGS && this.settingsMenu.hasChanges()) {
                    this.exitSettingsConfirmation.setVisible(true, true, AutosavePage.MIMIC);
                    let confirmationOption = await lastValueFrom(
                        this.exitSettingsConfirmation.onOptionSelected.pipe(take(1))
                    );
                    const canvas: any = this.canvasService.getCanvas();
                    if (confirmationOption === ConfirmationWindowTypes.DISCARD) {
                        await this.settingsMenu.discardSettings();
                        canvas.mouseUpEvent(new MouseEvent('mouseup'));
                    } else if (confirmationOption === ConfirmationWindowTypes.GO_BACK) {
                        canvas.mouseUpEvent(new MouseEvent('mouseup'));
                        this.selectionService.setSelectedElements(this.selectedElements);
                        return;
                    } else if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                        const result = await this.settingsMenu.saveSettings();
                        canvas.mouseUpEvent(new MouseEvent('mouseup'));
                        if (!result) {
                            this.selectionService.setSelectedElements(this.selectedElements);
                            return;
                        }
                    }
                }
                this.selectedElements = _.cloneDeep(elem);
                // Path select logic
                if (this.selectedElements[0]?.tagName === 'circle') {
                    const canvas: any = this.canvasService.getCanvas();
                    const pathObj = canvas.getPathObj();
                    if (pathObj?.elem) {
                        this.selectionService.setSelectedElements([pathObj.elem]);
                    }
                }
                if (elem.length > 1 || elem.length === 0) this.editorService.setCurrentSelectedTab(MenuTabs.DESIGN);
                if (!this.userCanEdit) this.editorService.setCurrentSelectedTab(MenuTabs.SETTINGS);
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.userCanEdit) {
            if (this.userCanEdit) {
                this.EDITOR_TAB = Object.values(MenuTabs);
                this.editorService.setCurrentSelectedTab(MenuTabs.DESIGN);
            } else {
                this.EDITOR_TAB = [MenuTabs.SETTINGS];
                this.editorService.setCurrentSelectedTab(MenuTabs.SETTINGS);
            }
        }
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    async tabClick(tabSelected: MenuTabs) {
        if (!this.userCanEdit || this.tabIsDisabled(tabSelected)) {
            return;
        }
        if (this.selectedMenu === MenuTabs.SETTINGS && this.settingsMenu.hasChanges()) {
            this.exitSettingsConfirmation.setVisible(true, true, AutosavePage.MIMIC);
            let confirmationOption = await lastValueFrom(this.exitSettingsConfirmation.onOptionSelected.pipe(take(1)));
            if (confirmationOption === ConfirmationWindowTypes.GO_BACK) {
                return;
            } else if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                await this.settingsMenu.saveSettings();
            }
        }
        this.editorService.setCurrentSelectedTab(tabSelected);
    }

    setSelectedMenu(tab: MenuTabs) {
        if (!this.userCanEdit) return;
        this.editorService.setCurrentSelectedTab(tab);
    }

    getSettingsMenuElement() {
        return this.settingsMenu;
    }

    getExitSettingsConfirmation() {
        return this.exitSettingsConfirmation;
    }

    tabIsDisabled(tab: MenuTabs) {
        if (!this.userCanEdit && tab === MenuTabs.DESIGN) return true;
        if (!this.userCanEdit && tab === MenuTabs.SETTINGS) return false;
        if (tab === MenuTabs.DESIGN) return false;
        return this.selectedElements.length > 1 || this.selectedElements.length === 0;
    }

    designTabIsOpen() {
        if (!this.userCanEdit) return false;
        return this.selectedMenu === MenuTabs.DESIGN;
    }

    settingsTabIsOpen() {
        return this.selectedMenu === MenuTabs.SETTINGS;
    }
}
